import { allActivityLogTypes } from '@publishing-engine/domain/models';

// Extended event type that includes properties from the actual API response
type ActivityEvent = {
  type: string;
  date: string;
  reviewRequestId?: string;
  subtype?: string;
  changes?: Array<Record<string, any>>;
  isOwner?: boolean;
  autoAssign?: boolean;
  revisionSeverity?: string;
  [key: string]: any;
};

/**
 * Custom title handlers for My Activity page
 * These handlers work with the actual activity log data from the API
 */
const MY_ACTIVITY_HANDLERS: Partial<
  Record<allActivityLogTypes, (event: ActivityEvent) => string>
> = {
  COCRecommendationSubmitted: event =>
    `${
      !event.isOwner && event.autoAssign ? 'Decision' : 'Recommendation'
    } Submitted - ${event.role ? event.role : 'COC'}`,

  RevisionRequested: event =>
    `Revision Requested${
      event.revisionSeverity ? ` (${event.revisionSeverity})` : ''
    }`,

  EditorialSubmissionDecision: event => {
    const decisionMap: Record<string, string> = {
      reject: 'Rejected',
      withdraw: 'Withdrawn',
      approveForPeerReview: 'Approved for Peer Review'
    };

    // Check if subtype is directly on event (actual API structure)
    const subtype = event.subtype as string | undefined;
    const decision = subtype ? decisionMap[subtype] || subtype : '';
    return `Decision Made${decision ? `: ${decision}` : ''}`;
  },

  SubmissionAssigned: event => {
    const [change1, change2] = event.changes || [];
    const subtype = change1?.subtype;
    const assignee = change2?.assignee || '';
    return `Submission ${
      subtype === 'owner' ? 'Assigned To' : 'Associated With'
    } ${assignee}`;
  },

  PeerReviewDecision: event => {
    const decisionMap: Record<string, string> = {
      acceptRequest: 'Accepted',
      declineRequest: 'Declined',
      autoAcceptRequest: 'Auto Accepted'
    };

    // Check if subtype is directly on event (actual API structure)
    const subtype = event.subtype as string | undefined;
    const decision = subtype ? decisionMap[subtype] || subtype : '';
    return `Peer Review Invitation${decision ? ` ${decision}` : ''}`;
  },

  RevisionSubmitted: event => {
    // Revision Form Submission
    if (event.subtype === 'fields') {
      return 'Revision Field Changed';
    }

    return 'Revision Submitted';
  },

  COCEditorAssigned: event => {
    return `Submission Assigned ${event.role ? `- ${event.role}` : '- Editor'}`;
  },

  SubmissionAssignedCOC: event => {
    return `Submission ${event.isSystem ? 'Auto ' : ''}Assigned ${
      event.role ? `- ${event.role}` : '- COC'
    }`;
  },

  COCRoleUnassigned: event => {
    return `Submission Unassigned ${event.role ? `- ${event.role}` : '- COC'}`;
  },

  ReviewRequested: () => 'Peer Review Invitation Sent',
  ReviewRequestEnqueued: () => 'Peer Reviewers Added to Queue',
  SubmissionCreated: () => 'New Submission Added',
  SubmissionApprovedForPreProduction: () =>
    'Decision Made: Proceed to Provisional Acceptance',
  SubmissionUnassigned: () => 'Submission Unassigned',
  SubmissionTagsReplaced: () => 'Submission Tags Edited',
  SubmissionDraftCreated: () => 'Submission draft created',
  SubmissionAccessed: () => 'Submission accessed',
  SubmissionDeadlinePassed: () => 'Submission Deadline Passed',
  SubmissionDeadlineExtensionRequested: () =>
    'Submission Deadline Extension Requested',
  SubmissionDeadlineExtended: () => 'Submission Deadline Extended',
  SubmissionDeadlineExtensionDenied: () =>
    'Submission Deadline Extension Request Declined',
  SubmissionReminderTriggered: () => 'Submission reminder triggered',

  EditorialSubmissionRejected: () => 'Decision Made: Reject',
  EditorialSubmissionWithdrawn: () => 'Decision Made: Withdraw',
  EditorialSubmissionWithdrawalAccepted: () =>
    "Decision Made: Withdraw at Author's Request",
  EditorialSubmissionUnsubmitted: () => 'Decision Made: Unsubmit',
  AuthorSubmissionWithdrawalRequested: () =>
    'Decision Made: Request Withdrawal',
  AuthorSubmissionWithdrawalRequestRevoked: () =>
    'Decision Made: Withdrawal Request Cancelled',
  DecisionReverted: event => {
    const { previousPhase = '', currentPhase = '' } = event.eventPayload || {};
    return `Decision Reversal: ${previousPhase} -> ${currentPhase}`;
  },

  DeclinedReviewInvitationResent: () => 'Peer Review Invitation Resent',
  ExpiredReviewInvitationResent: () => 'Peer Review Invitation Resent',
  WithdrawnReviewInvitationResent: () => 'Peer Review Invitation Resent',
  EnqueuedReviewerRemoved: () => 'Reviewer Removed from Queue',
  QueueToggled: event =>
    `Automatic Queue ${event.active ? 'Activated' : 'Deactivated'}`,
  MinReviewCountSet: () => `Recommended Number of Reviews Edited`,

  CoReviewDecision: event => {
    const decisionMap: Record<string, string> = {
      acceptRequest: 'Accepted',
      declineRequest: 'Declined'
    };
    const subtype = event.subtype as string | undefined;
    const decision = subtype ? decisionMap[subtype] || subtype : '';
    return `Co-Reviewer Invitation${decision ? ` ${decision}` : ''}`;
  },
  FirstAssessmentSubmitted: () => 'Peer Review First Assessment Submitted',
  ReviewRated: () => "Reviewer's Review Rated",
  RescindedFirstAssessment: () => "Reviewer's Review Rescinded",
  PeerReviewModerated: () => "Reviewer's Author Feedback Moderated",
  ReviewRemoved: () => 'Review Removed',
  ReviewDeadlinePassed: () => 'Review Deadline Passed',
  ReviewDeadlineExtensionRequested: () => 'Review Deadline Extension Requested',
  ReviewDeadlineExtended: () => 'Review Deadline Extended',
  ReviewDeadlineExtensionDenied: () =>
    'Review Deadline Extension Request Declined',
  ReviewInRevisionRequested: () => 'Review In Revision Requested',
  ReviewReminderTriggered: () => 'Review reminder triggered',
  PeerReviewInterrupted: () => 'Peer Review Round Interrupted',
  EditorialInvitedReviewInvitationWithdrawn: () =>
    'Reviewer Withdrawn from Peer Review',
  EditorialAcceptedReviewInvitationWithdrawn: () =>
    'Reviewer Withdrawn from Peer Review',
  EditorialAwaitingRevisionReviewInvitationWithdrawn: () =>
    'Reviewer Withdrawn from Peer Review',
  EditorialRevisionInvitedReviewInvitationWithdrawn: () =>
    'Reviewer Withdrawn from Peer Review',
  EditorialRevisionAssessmentReviewInvitationWithdrawn: () =>
    'Reviewer Withdrawn from Peer Review',

  CoReviewerInvited: () => 'Co-Reviewer Invited',

  RevisionReviewRequested: () => 'Revision Review Invitation Resent',
  RevisionAssessmentSubmitted: () => 'Revision Review Submitted',
  EditorialImmediateRevisionRequested: () =>
    `Decision Made: Immediate Revision`,

  EditorialTransferOffered: () => 'Decision Made: Transfer Offered',
  TransferOfferAccepted: () => 'Transfer Offer Accepted',
  TransferDeclined: () => 'Transfer Offer Rejected',
  LicensingSubmitted: () => 'Copyright Information Edited',
  AuthorCopyrightSubmitted: () => 'Copyright Form Submitted By Author',
  AuthorCopyrightUploaded: () => 'Copyright Form Uploaded By Author',
  AuthorCopyrightReset: () => 'Copyright Form Deleted',
  EditorialCopyrightRequested: () => 'Copyright Form Requested By Editor',
  EditorialCopyrightUploaded: () => 'Copyright Form Uploaded By Editor',
  AuthorICMJEUploaded: () => 'Forms and Disclosures Uploaded By Author',
  EditorialICMJEUploaded: () => 'Forms and Disclosures Uploaded By Editor',
  EditorialICMJERequested: () => 'Form and Disclosures Requested By Editor',

  SubmissionNote: () => 'Note Action',
  AdhocEmailSent: () => 'Ad hoc Email Sent',
  ContactJournal: () => 'Submission issue or query sent to journal',

  EarlyAssessmentRequested: () => 'Early Assessment Requested',
  EarlyAssessmentRequestDecision: () => 'Early Assessment Request Decision',
  EarlyAssessmentSubmitted: () => 'Early Assessment Submitted',
  EarlyAssessmentReminderTriggered: () => 'Early Assessment reminder triggered',

  AdjudicationRequested: () => 'Adjudication Requested',
  AdjudicationRequestDecision: () => 'Adjudication Request Decision',
  AdjudicationSubmitted: () => 'Adjudication Submitted',
  AdjudicationReminderTriggered: () => 'Adjudication reminder triggered',

  AcceptedForFormalAcceptance: () =>
    'Decision Made: Proceed to Formal Acceptance',

  ArticleTypeChanged: () => 'Submission Manuscript Type Edited',
  IntegrationResult: () => 'Integration Result',

  COCRecommendationModerated: () => 'Editor Recommendation Moderated'
};

export const getMyActivityTitle = (
  eventType: allActivityLogTypes,
  event: ActivityEvent
): string | null => {
  const handler = MY_ACTIVITY_HANDLERS[eventType];
  return handler ? handler(event) : null;
};
